import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ProductShowcase.css';

const features = [
    'Электронные журналы и расписание',
    'Система тестирования с ИИ-проверкой',
    'Мессенджер и push-уведомления',
    'Доступность на Web, iOS, Android, Desktop',
    'ИИ-ассистент и рекомендации'
];

const screens = [
    {
        id: 'home',
        src: '/screens/home.png',
        label: 'Главная',
        caption: 'Дашборд студента — оценки, посещаемость и быстрый доступ к модулям',
    },
    {
        id: 'schedule',
        src: '/screens/schedule.png',
        label: 'Расписание',
        caption: 'Недельное расписание и оценки с подсветкой текущих занятий',
    },
    {
        id: 'lectures',
        src: '/screens/lectures.png',
        label: 'Лекции',
        caption: 'Материалы по дисциплинам: разделы, темы и лекции в одном месте',
    },
    {
        id: 'practices',
        src: '/screens/practices.png',
        label: 'Практика',
        caption: 'Практические работы: задание, бланк отчёта и отправка решения',
    },
    {
        id: 'login',
        src: '/screens/login.png',
        label: 'Вход',
        caption: 'Безопасный вход в образовательную экосистему Колледж Плюс',
    },
];

const AUTO_MS = 4500;

function ProductShowcase() {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const [visible, setVisible] = useState(false);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const sectionRef = useRef(null);
    const frameRef = useRef(null);
    const touchStartX = useRef(null);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (paused || !visible) return undefined;
        const id = setInterval(() => {
            setActive((prev) => (prev + 1) % screens.length);
        }, AUTO_MS);
        return () => clearInterval(id);
    }, [paused, visible, active]);

    const goTo = useCallback((index) => {
        setActive((index + screens.length) % screens.length);
    }, []);

    const handleMouseMove = (e) => {
        const frame = frameRef.current;
        if (!frame) return;
        const rect = frame.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        setTilt({ x: py * -5, y: px * 7 });
    };

    const handleMouseLeave = () => {
        setTilt({ x: 0, y: 0 });
        setPaused(false);
    };

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        setPaused(true);
    };

    const handleTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 40) {
            goTo(active + (dx < 0 ? 1 : -1));
        }
        touchStartX.current = null;
        setPaused(false);
    };

    const current = screens[active];

    return (
        <section
            className={`showcase ${visible ? 'showcase--visible' : ''}`}
            id="product"
            ref={sectionRef}
        >
            <div className="showcase-glow" aria-hidden="true" />
            <div className="showcase-inner">
                <div className="showcase-text">
                    <span className="section-label showcase-reveal" style={{ '--d': '0s' }}>
                        Наш продукт
                    </span>
                    <h2 className="section-title showcase-reveal" style={{ '--d': '0.08s' }}>
                        Колледж Плюс —<br />образовательная<br />экосистема
                    </h2>
                    <p className="section-sub showcase-reveal" style={{ '--d': '0.16s' }}>
                        Полноценная платформа для управления учебным процессом, разработанная командой AIColl. Используется в реальных учебных заведениях.
                    </p>

                    <ul className="showcase-list">
                        {features.map((feature, index) => (
                            <li
                                key={feature}
                                className="showcase-reveal"
                                style={{ '--d': `${0.22 + index * 0.06}s` }}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                {feature}
                            </li>
                        ))}
                    </ul>

                    <a
                        href="/app.html"
                        className="btn btn-accent showcase-reveal"
                        style={{ '--d': '0.55s' }}
                    >
                        Открыть приложение
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    </a>
                </div>

                <div className="showcase-visual-wrap showcase-reveal" style={{ '--d': '0.2s' }}>
                    <div
                        className="showcase-visual"
                        onMouseEnter={() => setPaused(true)}
                        onMouseLeave={handleMouseLeave}
                        onMouseMove={handleMouseMove}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div
                            className="app-frame"
                            ref={frameRef}
                            style={{
                                transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                            }}
                        >
                            <div className="app-frame-bar">
                                <span className="app-frame-dots" aria-hidden="true">
                                    <i /><i /><i />
                                </span>
                                <span className="app-frame-title">Колледж Плюс · {current.label}</span>
                                <span className="app-frame-live">
                                    <span className="app-frame-live-dot" />
                                    Live UI
                                </span>
                            </div>

                            <div className="app-frame-stage" role="region" aria-live="polite" aria-label={current.caption}>
                                {screens.map((screen, index) => (
                                    <img
                                        key={screen.id}
                                        src={screen.src}
                                        alt={screen.caption}
                                        className={`app-shot ${index === active ? 'is-active' : ''}`}
                                        loading={index === 0 ? 'eager' : 'lazy'}
                                        draggable={false}
                                    />
                                ))}

                                <div className="app-frame-shine" aria-hidden="true" />

                                <button
                                    type="button"
                                    className="app-nav app-nav--prev"
                                    onClick={() => goTo(active - 1)}
                                    aria-label="Предыдущий экран"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M15 18l-6-6 6-6" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className="app-nav app-nav--next"
                                    onClick={() => goTo(active + 1)}
                                    aria-label="Следующий экран"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </button>

                                <div className="app-dots" role="tablist" aria-label="Слайды">
                                    {screens.map((screen, index) => (
                                        <button
                                            key={`dot-${screen.id}`}
                                            type="button"
                                            className={`app-dot ${index === active ? 'is-active' : ''}`}
                                            onClick={() => goTo(index)}
                                            aria-label={screen.label}
                                            aria-current={index === active ? 'true' : undefined}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="app-frame-progress" aria-hidden="true">
                                <div
                                    key={active}
                                    className={`app-frame-progress-bar ${paused ? 'is-paused' : ''}`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ProductShowcase;
